/**
 * propertyProximity — PURE haversine distance + nearby-property finder (AD-036).
 *
 * SDK-free + JSX-free so it is directly unit-testable (node --test / loadTs).
 *
 * Used by the Property step to warn staff that a freshly entered location is
 * within R metres of an existing property (registry dedup). The scan INCLUDES
 * archived properties (owner ruling) — each match is flagged ACTIVE / ARCHIVED
 * so staff can link or knowingly create a new property.
 */

import type { LiveProperty } from './propertyModel';

/** A geographic coordinate in decimal degrees. */
export interface LatLng {
  lat: number;
  lng: number;
}

/** Default proximity radius — "same plot only" (owner ruling, settings-adjustable). */
export const DEFAULT_PROXIMITY_RADIUS_M = 25;

const EARTH_RADIUS_M = 6_371_000; // mean Earth radius in metres

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * PURE — great-circle distance between two lat/lng points, in METRES (haversine).
 * Returns a non-negative number. Symmetric: dist(a,b) === dist(b,a).
 */
export function haversineMetres(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_M * c;
}

/** A single proximity match — an existing property within the radius. */
export interface NearbyMatch {
  property: LiveProperty;
  /** Distance from the query coordinate, in metres (rounded to 1 dp). */
  distanceM: number;
  /** Whether the matched property is archived (owner: show ALL incl. archived). */
  archived: boolean;
}

/**
 * PURE — extract a usable {lat,lng} from a property's siteLocation, or null
 * when it has no decimal coordinates.
 */
export function propertyCoord(prop: LiveProperty): LatLng | null {
  const sl = prop.siteLocation;
  if (!sl) return null;
  if (typeof sl.lat === 'number' && typeof sl.lng === 'number' &&
      Number.isFinite(sl.lat) && Number.isFinite(sl.lng)) {
    return { lat: sl.lat, lng: sl.lng };
  }
  return null;
}

/**
 * PURE — find all properties whose site location is within `radiusM` of the
 * query coordinate, sorted nearest-first.
 *
 * - Properties without coordinates are skipped.
 * - The `excludePropertyId` (the one currently being edited / linked) is excluded.
 * - Archived properties ARE included (owner ruling); each match carries an
 *   `archived` flag so the UI can badge ACTIVE / ARCHIVED.
 */
export function nearbyProperties(
  coord: LatLng,
  props: LiveProperty[],
  radiusM: number = DEFAULT_PROXIMITY_RADIUS_M,
  excludePropertyId?: string,
): NearbyMatch[] {
  if (!coord || !Number.isFinite(coord.lat) || !Number.isFinite(coord.lng)) return [];
  const out: NearbyMatch[] = [];
  for (const prop of props) {
    if (excludePropertyId && prop.propertyId === excludePropertyId) continue;
    const pc = propertyCoord(prop);
    if (!pc) continue;
    const d = haversineMetres(coord, pc);
    if (d <= radiusM) {
      out.push({
        property: prop,
        distanceM: Math.round(d * 10) / 10,
        archived: prop.archived === true,
      });
    }
  }
  out.sort((a, b) => a.distanceM - b.distanceM);
  return out;
}
